import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../redux/slices/authSlice';
import AuthService from '../../services/authService';
import SessionManager from '../../services/sessionManager';
import VTOAuthHandler from '../../services/VTOAuthHandler';
import './AuthCallback.css';

/**
 * AuthCallback component handles authentication return from SHEYOU platform
 * Processes the JWT token and redirects to appropriate page
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Verifying your credentials...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        const returnUrl = urlParams.get('returnUrl') || urlParams.get('return_to');

        if (!token) {
          throw new Error('No authentication token received');
        }

        const authService = new AuthService();
        const sessionManager = new SessionManager();
        const vtoAuthHandler = new VTOAuthHandler();

        setMessage('Validating authentication...');
        const authResult = await authService.handleAuthReturn(token);

        if (!authResult.success) {
          throw new Error(authResult.error || 'Authentication validation failed');
        }

        // Store session data
        const sessionStored = sessionManager.handleAuthCallback(token, authResult.user);
        if (!sessionStored) {
          throw new Error('Failed to store authentication session');
        }

        localStorage.setItem('user', authResult.user.id);
        localStorage.setItem('token', token);
        localStorage.setItem('success', 'true');

        // Set credentials in Redux
        dispatch(setCredentials({ token, user: authResult.user }));

        // Check for VTO restoration
        const vtoRestoration = vtoAuthHandler.checkForVTORestoration();
        let redirectTo = '/my-closet';

        if (vtoRestoration.shouldRestore && vtoRestoration.isValid) {
          const vtoCallbackResult = await vtoAuthHandler.handleAuthCallback(token);

          if (vtoCallbackResult.success && vtoCallbackResult.contextRestoration.success) {
            const vtoContext = vtoCallbackResult.contextRestoration.context;
            const vtoParams = new URLSearchParams();
            vtoParams.set('vto_restore', 'true');
            vtoParams.set('vto_operation', vtoContext.operation.type);
            vtoParams.set('vto_timestamp', Date.now().toString());
            redirectTo = `/my-closet?${vtoParams.toString()}`;
            vtoAuthHandler.cleanupUrlParameters();
          }
        } else if (returnUrl) {
          // Browser extension callback: redirect directly to extension URL with token
          const isExtensionUrl = returnUrl &&
            (returnUrl.startsWith('chrome-extension://') || returnUrl.includes('.chromiumapp.org'));
          if (isExtensionUrl) {
            const separator = returnUrl.includes('?') ? '&' : '?';
            window.location.href = `${returnUrl}${separator}token=${token}`;
            return;
          }
          try {
            const url = new URL(returnUrl);
            const urlPath = url.pathname + url.search;
            if (urlPath !== '/' && urlPath !== '') {
              redirectTo = urlPath;
            }
          } catch (e) {
            // use default
          }
        }

        setStatus('success');
        setMessage('Welcome to SUSU Closet!');

        setTimeout(() => {
          navigate(redirectTo);
        }, 1200);

      } catch (error) {
        console.error('Authentication callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Authentication failed.');
      }
    };

    handleAuthCallback();
  }, [location, navigate, dispatch]);

  return (
    <div className="auth-callback-page">
      <div className="auth-callback-card">
        {status === 'processing' && (
          <>
            <div className="auth-callback-spinner" />
            <h2 className="auth-callback-title">Signing in...</h2>
            <p className="auth-callback-message">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="auth-callback-checkmark">✓</div>
            <h2 className="auth-callback-title">Welcome!</h2>
            <p className="auth-callback-message">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="auth-callback-error-icon">✕</div>
            <h2 className="auth-callback-title">Sign in failed</h2>
            <p className="auth-callback-message auth-callback-error-text">{message}</p>
            <button className="auth-callback-home-btn" onClick={() => navigate('/')}>
              Return to SUSU Closet
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;